import { useEffect, useState } from 'react';

interface UseDismissibleEducationalTooltipOptions {
  storageKey: string;
  isEnabled?: boolean;
}

const wasDismissed = (storageKey: string) => {
  try {
    return localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

/**
 * Keeps launch education visible until the user explicitly dismisses it.
 * Dismissal is browser-local and intentionally survives remounts and reloads.
 */
export const useDismissibleEducationalTooltip = ({
  storageKey,
  isEnabled = true,
}: UseDismissibleEducationalTooltipOptions) => {
  const [isDismissed, setIsDismissed] = useState(() =>
    wasDismissed(storageKey)
  );

  useEffect(() => {
    if (!isEnabled || isDismissed) return;

    try {
      if (localStorage.getItem(storageKey) === null) {
        localStorage.setItem(storageKey, 'false');
      }
    } catch {
      // Storage can be unavailable in restricted browser contexts. The
      // education still works for the current mount in that case.
    }
  }, [isDismissed, isEnabled, storageKey]);

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, 'true');
    } catch {
      // Hiding the current tooltip must not depend on storage availability.
    }
    setIsDismissed(true);
  };

  return { isOpen: isEnabled && !isDismissed, dismiss };
};
